import { ChangeDetectionStrategy, Component, inject, OnInit, WritableSignal, signal } from '@angular/core';
import { ContextService } from '../../../services/context.service';
import {
  AddLoadunitDto,
  DeleteLoadunitError,
  LoadunitDto,
  LoadunitType,
  MasterdataService,
  UpdateLoadunitDto,
  UpdateLoadunitError,
} from '../../../shared/generated/backend';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderComponent } from '../../../loader/loader.component';
import { confirm } from 'devextreme/ui/dialog';

import { SharedModule } from '../../../shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { DxFormModule, DxSortableModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { LoadUnitRefreshService } from './loadunit-refresh.service';

interface LoadunitTypeWrapper {
  display: string;
  value: LoadunitType;
}

@Component({
  selector: 'app-loadunit-context-view',
  imports: [LoaderComponent, SharedModule, FormsModule, DxFormModule, DxSortableModule],
  templateUrl: './loadunit-context-view.component.html',
  styleUrl: './loadunit-context-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadunitContextViewComponent implements OnInit {
  readonly loadunitId: WritableSignal<number | null> = signal(null);
  readonly isLoading: WritableSignal<boolean> = signal(false);
  isDirty!: boolean;
  readonly _loadunitTypes = [
    LoadunitType.NotSpecified,
    LoadunitType.ExternalUnit,
    LoadunitType.PhysicalUnit,
    LoadunitType.PlanningUnit,
    LoadunitType.ForTourPlanning,
  ];
  loadunitTypes: Array<LoadunitTypeWrapper> = [];
  formData: LoadunitDto = { id: 0, name: '', type: LoadunitType.NotSpecified, maxLoad: 0, maxVolume: 0 };
  dto!: LoadunitDto;
  type: 'create' | 'edit' = 'create';

  private contextService = inject(ContextService);
  private masterDataService = inject(MasterdataService);
  private activatedRoute = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private loadUnitRefreshService = inject(LoadUnitRefreshService);

  async ngOnInit() {
    // Making sure that the dropdown values are translated
    this.loadunitTypes = await Promise.all(
      this._loadunitTypes.map(async (t, _) => {
        const text = await firstValueFrom(this.translateService.get('MASTERDATA.LOADUNIT.DTO.TYPE_' + t));
        return {
          value: t,
          display: text,
        };
      })
    );

    this.activatedRoute.params.subscribe(async params => {
      this.isDirty = false;
      this.isLoading.set(false);
      const id = params['loadunitId'];
      if (id) {
        this.type = 'edit';
        this.loadunitId.set(id);
        await this.loadLoadunit();
      } else {
        this.type = 'create';
        //this.formData = { id: 0, name: '', type: LoadunitType.NotSpecified, maxLoad: 0, maxVolume: 0 };
      }
    });
  }

  async loadLoadunit() {
    this.isLoading.set(true);
    this.dto = await firstValueFrom(this.masterDataService.getLoadunit(this.loadunitId()!));
    this.formData = { ...this.dto };
    this.isLoading.set(false);
  }

  async delete() {
    const id = this.loadunitId();
    if (id === null) return;
    const confirmed = await confirm(
      this.translateService.instant('MASTERDATA.LOADUNIT.CONTEXT.DELETE_PROMPT'),
      this.translateService.instant('MASTERDATA.LOADUNIT.CONTEXT.DELETE_TITLE')
    );
    if (!confirmed) return;
    this.isLoading.set(true);
    try {
      await firstValueFrom(this.masterDataService.deleteLoadunit(id));
      this.refreshTableAndOpenView();
    } catch (err: any /*eslint-disable-line @typescript-eslint/no-explicit-any*/) {
      if (err.status === 409) {
        const innerError = err.error as DeleteLoadunitError;
        notify(
          {
            message:
              this.translateService.instant('MASTERDATA.LOADUNIT.CONTEXT.ERROR_IN_USE_BY') +
              innerError.usedBy.join(', '),
          },
          'error',
          10_000
        );
      }
    } finally {
      this.isLoading.set(false);
      this.loadUnitRefreshService.triggerRefresh();
      this.close();
    }
  }

  async save() {
    if (!this.isDirty) return;

    // Custom validation logic
    if (!this.formData.name || !this.formData.type || !this.formData.maxLoad || !this.formData.maxVolume) {
      notify({
        message: this.translateService.instant('SCHEDULE.ALL_FIELDS_REQUIRED'),
        type: 'error',
        displayTime: 3000,
      });
      this.isLoading.set(false);
      return;
    }

    try {
      this.isLoading.set(true);
      if (this.type === 'create') {
        const data: AddLoadunitDto = {
          name: this.formData.name,
          type: this.formData.type,
          maxLoad: this.formData.maxLoad,
          maxVolume: this.formData.maxVolume,
        };
        await firstValueFrom(this.masterDataService.addLoadunit(data));
        this.loadUnitRefreshService.triggerRefresh();
        this.close();
      } else if (this.type === 'edit') {
        const data: UpdateLoadunitDto = {
          newId: this.formData.id,
          name: this.formData.name,
          type: this.formData.type,
          maxLoad: this.formData.maxLoad,
          maxVolume: this.formData.maxVolume,
          TrapoLoadunitType: 0,
        };
        await firstValueFrom(this.masterDataService.updateLoadunit(this.dto.id, data));
        this.loadUnitRefreshService.triggerRefresh();
        this.close();
      }
    } catch (err: any /*eslint-disable-line @typescript-eslint/no-explicit-any*/) {
      if (err.status === 409 || err.error === UpdateLoadunitError.NameAlreadyUsed) {
        notify({
          message: this.translateService.instant('MASTERDATA.LOADUNIT.CONTEXT.ERROR_NAME_USED'),
          type: 'error',
          displayTime: 3000,
        });
      } else {
        console.error('Error: ', err);
        notify({
          message: this.translateService.instant('ERROR_STATUS.Unkown_Error'),
          type: 'error',
          displayTime: 3000,
        });
      }
    } finally {
      this.isLoading.set(false);
      this.close();
    }
  }

  async refreshTableAndOpenView(id?: number) {
    // console.log('refreshTableAndOpenView called with id:', id); // Debugging statement
    this.close();
    await this.router.navigateByUrl('/masterdata/loadunits', { onSameUrlNavigation: 'reload' });
    if (id !== undefined) {
      // console.log('Navigating to loadunit with id:', id); // Debugging statement
      this.contextService.openContextView(`masterdata/loadunits/loadunit/${id}`);
    }
  }

  close() {
    this.contextService.closeAllLoadunitContextViews();
  }
}
